import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Courses", href: "#courses", id: "courses" },
  { label: "Method", href: "#method", id: "method" },
  { label: "Stories", href: "#stories", id: "stories" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -20, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/50 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} className="flex items-center" data-testid="nav-logo" aria-label="Edvio home">
          <img src="/edvio-logo-wide.png" alt="Edvio Language Institute" className="h-14 w-auto" />
        </button>
        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.href)}
              className="relative text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#F4F4F0] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#C44900] after:transition-[width] after:duration-300 hover:after:w-full"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            className="group flex items-center gap-2 bg-[#C44900] text-[#F4F4F0] text-xs uppercase tracking-[0.18em] px-6 py-3 rounded-full hover:bg-[#e05600] transition-colors duration-300"
            data-testid="nav-enquire-button"
          >
            Enquire
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>
        <button className="md:hidden text-[#F4F4F0]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden backdrop-blur-xl bg-black/80 border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.href)}
                  className="text-left text-sm uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#F4F4F0] transition-colors"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 bg-[#C44900] text-[#F4F4F0] text-xs uppercase tracking-[0.18em] px-6 py-3 rounded-full w-fit"
                data-testid="nav-mobile-enquire-button"
              >
                Enquire Now
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
